import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  FieldPolicy,
  fromPromise,
  InMemoryCache,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { AxiosError } from 'axios';
import { HttpCodes } from 'services/api/enums';
import { store } from 'store';
import { getNewAccessTokenUsingRefreshToken } from 'utils';

import { IQueryWithPagination } from '../../types/common';
import cleanTypenameLink from './helper';

const withPagination: FieldPolicy = {
  keyArgs: false,
  merge(existing: IQueryWithPagination, incoming: IQueryWithPagination) {
    const { page = 1, take = 10 } = incoming.meta || {};
    if (page === 1) return incoming;
    const offset = (page - 1) * take;

    // Slicing is necessary because the existing data is
    // immutable, and frozen in development.
    const merged = existing ? existing.data?.slice(0) || [] : [];
    incoming.data.forEach((row: any, i: number) => {
      merged[offset + i] = row;
    });

    return {
      data: merged,
      meta: incoming.meta,
    };
  },
  read(existing: IQueryWithPagination) {
    if (!(existing && Array.isArray(existing.data))) return existing;
    const { page = 1, take = 10 } = existing.meta || {};
    if (page === 1) return existing;

    const { meta, data } = existing;
    return {
      data: data?.slice((page - 1) * take, page * take),
      meta,
    };
  },
};

function configureApolloClient() {
  const httpLink = createHttpLink({
    uri: process.env.REACT_APP_BACKEND_GRAPHQL_URL,
  });

  const wsLink = new WebSocketLink({
    uri: process.env.REACT_APP_BACKEND_GRAPHQL_WS || 'ws://localhost:3001/graphql',
    options: {
      reconnect: true,
    },
  });

  // The split function takes three parameters:
  //
  // * A function that's called for each operation to execute
  // * The Link to use for an operation if the function returns a "truthy" value
  // * The Link to use for an operation if the function returns a "falsy" value
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink,
  );

  let isRefreshing = false;
  const pendingRequests: {
    reject: (error: AxiosError | null) => void;
    resolve: (token: string | null) => void;
  }[] = [];
  const resolvePendingRequests = (error: AxiosError | null, token: string | null) => {
    pendingRequests.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve(token);
      }
    });
    pendingRequests.length = 0; // Clear array
  };
  // eslint-disable-next-line consistent-return
  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      // eslint-disable-next-line no-restricted-syntax
      const graphQLError = graphQLErrors[0];
      const { message, locations, path, extensions } = graphQLError;
      switch (extensions?.exception?.status) {
        case HttpCodes.UNAUTHORIZED: {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Token invalid expired');
          }
          if (isRefreshing) {
            return fromPromise(
              new Promise((resolve, reject) => {
                pendingRequests.push({ resolve, reject });
              }),
            ).flatMap((newAccessToken) => {
              if (newAccessToken) {
                operation.setContext(({ headers = {} }) => ({
                  headers: {
                    ...headers,
                    authorization: `Bearer ${newAccessToken}`,
                  },
                }));
              }
              return forward(operation);
            });
          }
          isRefreshing = true;
          return fromPromise(
            new Promise((resolve) =>
              getNewAccessTokenUsingRefreshToken()
                .then((response: any) => {
                  const newAccessToken = response?.data?.accessToken;
                  resolve(newAccessToken);
                })
                .catch((error) => {
                  resolvePendingRequests(error, null);
                })
                .finally(() => {
                  isRefreshing = false;
                }),
            ),
          ).flatMap((newAccessToken) => {
            if (newAccessToken) {
              operation.setContext(({ headers = {} }) => ({
                headers: {
                  ...headers,
                  authorization: `Bearer ${newAccessToken}`,
                },
              }));
            }
            if (pendingRequests.length > 0) {
              resolvePendingRequests(null, newAccessToken as string);
            }
            return forward(operation);
          });
        }
        default: {
          if (process.env.NODE_ENV !== 'production') {
            console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
          }
          break;
        }
      }
    }
    if (networkError) console.log(`[Network error]: ${networkError}`);
  });

  const authLink = setContext(async (_, { headers }) => {
    // get the authentication token from local storage if it exists
    const { accessToken } = store.getState().auth;

    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
    };
  });

  const client = new ApolloClient({
    link: ApolloLink.from([cleanTypenameLink, authLink, errorLink, splitLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            findUserEntity: withPagination,
            findTransactionEntity: withPagination,
          },
        },
      },
    }),
  });

  return client;
}

export default configureApolloClient();
